import { makeAutoObservable, runInAction } from 'mobx'
import {
  type ChatbotActions,
  type IChatbotActionModel,
  ChatbotActionType,
  type IChatbotActionType,
  type ChatbotErrorsList,
  type IChatbotActionSettings,
} from 'entities/Chatbot'
import { type AgentActions } from 'entities/Chatbot/model/AgentActions'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ChatbotActionMessageStore } from './chatbotActionMessageStore'
import { ChatbotActionTagStore } from './chatbotActionTagStore'
import { ChatbotActionNoteStore } from './chatbotActionNoteStore'

export type IChatbotActionStores =
  | ChatbotActionMessageStore
  | ChatbotActionNoteStore
  | ChatbotActionTagStore

export type ChatbotStoreTypeMap = {
  [ChatbotActionType.AddTag]: ChatbotActionTagStore
  [ChatbotActionType.RemoveTag]: ChatbotActionTagStore
  [ChatbotActionType.ContactNote]: ChatbotActionNoteStore
  [ChatbotActionType.SendMessage]: ChatbotActionMessageStore
}

export class ChatbotAction {
  private static _storeFactory(
    model: IChatbotActionModel,
    entities: IEntitiesStore
  ): IChatbotActionStores {
    switch (model.type) {
      case ChatbotActionType.RemoveTag:
      case ChatbotActionType.AddTag:
        return new ChatbotActionTagStore(model)
      case ChatbotActionType.SendMessage:
        return new ChatbotActionMessageStore(model, entities)
      case ChatbotActionType.ContactNote:
        return new ChatbotActionNoteStore(model, entities)
    }
  }

  private _storesMap = new Map<IChatbotActionModel, IChatbotActionStores>()

  constructor(
    private _actionsModel: ChatbotActions | AgentActions,
    public actionErrors: ChatbotErrorsList<IChatbotActionModel, IChatbotActionSettings>
  ) {
    makeAutoObservable(this)
  }

  get list() {
    return this._actionsModel.actions
  }

  getActionStore<T extends IChatbotActionModel>(
    model: T,
    entities: IEntitiesStore
  ): ChatbotStoreTypeMap[T['type']] {
    const store = this._storesMap.get(model) ?? ChatbotAction._storeFactory(model, entities)

    runInAction(() => this._storesMap.set(model, store))

    return store as ChatbotStoreTypeMap[T['type']]
  }

  createAction = (type: IChatbotActionType, entities: IEntitiesStore) => {
    const model = this._actionsModel.addAction(type)
    const store = ChatbotAction._storeFactory(model, entities)

    this._storesMap.set(model, store)
  }

  removeAction = (index: number) => {
    const model = this._actionsModel.removeAction(index)
    const store = this._storesMap.get(model)

    if (!store) return

    this._storesMap.delete(model)
    store?.dispose()
  }
}
